import React from 'react';
import { Link } from 'react-router-dom';

export const meta = {
  slug: 'easements-explained',
  title: 'Easements explained: what E-1 on your plan actually means',
  navTitle: 'Easements explained',
  description:
    'What easements are, how to read E-1 and E-2 labels on a Victorian plan of subdivision, the common types, and what an easement means for pools, sheds and extensions.',
  published: '2026-07-14',
  readingTime: '7 min read',
  teaser:
    'How to read the hatched strips and E-1 labels on a plan of subdivision — and what they mean for your pool, shed or extension plans.',
};

export default function EasementsExplained() {
  return (
    <>
      <p className="guide-lede">
        Somewhere in most Section 32 bundles is a surveyed drawing with hatched strips
        labelled E-1 or E-2, and a schedule describing an "easement for sewerage 1.83
        metres wide". Those few lines can decide whether you can ever build the extension
        you're planning. Here's how to read them.
      </p>

      <h2>What an easement is</h2>
      <p>
        An easement is a legal right that someone else holds over part of your land —
        without owning it. The classic examples: the water authority's right to run a
        sewer under your back yard and dig it up when it needs maintenance, or a
        neighbour's right to drive across a strip of your lot to reach theirs. You still
        own the land under an easement; you just can't use it in ways that interfere with
        the right.
      </p>

      <h2>How easements appear in a Section 32</h2>
      <p>They surface in up to three places, and it's worth checking all of them:</p>
      <ul>
        <li>
          <strong>On the title</strong> — the Register Search Statement lists easements
          among "encumbrances, liens and interests", often as a bare reference like
          "E-1 on PS 543210" following the words <em>SUBJECT TO</em>.
        </li>
        <li>
          <strong>On the plan of subdivision</strong> — the drawing shows each easement as
          a marked strip along a boundary (or occasionally through the middle of the lot),
          labelled E-1, E-2 and so on, with its width in metres. The plan's easement
          schedule then describes each label: its purpose, width and who benefits.
        </li>
        <li>
          <strong>In authority certificates</strong> — the water authority search near the
          back of the bundle routinely discloses sewerage and drainage easements, and is
          sometimes the only place an implied easement is mentioned.
        </li>
      </ul>
      <p>
        That last point matters: on older subdivisions, some easements are{' '}
        <em>implied</em> rather than drawn — sewers and drains that legally burden the
        land even though no strip appears on the plan. If the water authority's asset
        plan shows a pipe crossing the lot, treat it as real regardless of what the
        subdivision plan shows.
      </p>

      <h2>The common types, decoded</h2>
      <ul>
        <li>
          <strong>Sewerage easement</strong> — the most common in suburban Victoria,
          usually 1.8–3 metres along a rear or side boundary, in favour of the water
          corporation.
        </li>
        <li>
          <strong>Drainage easement</strong> — carries stormwater, in favour of council or
          the water authority; often shares a strip with the sewerage easement.
        </li>
        <li>
          <strong>Carriageway easement / right of way</strong> — someone else may pass
          over your land, typically a shared driveway or rear laneway. Check who benefits
          and whether maintenance is shared.
        </li>
        <li>
          <strong>Party wall easement</strong> — mutual rights over a shared wall between
          attached dwellings.
        </li>
        <li>
          <strong>Utility easements "in gross"</strong> — powerlines, gas mains or
          pipelines in favour of an authority rather than a neighbouring lot.
        </li>
      </ul>

      <h2>What an easement means for you, practically</h2>
      <ul>
        <li>
          <strong>Building over one usually needs consent.</strong> For sewer and drainage
          easements, that means a build-over consent from the water authority — commonly
          granted for lightweight structures like carports, routinely refused for
          habitable rooms and pools over the asset. No consent can mean demolition at your
          cost.
        </li>
        <li>
          <strong>The authority can dig it up.</strong> They must reinstate, but
          "reinstate" means backfill and basic surface repair — not rebuilding your
          landscaped garden or paving.
        </li>
        <li>
          <strong>It constrains your site plan permanently.</strong> A 2.5-metre easement
          along the rear of a 30-metre-deep lot removes a strip of buildable area from
          every future plan — extension, granny flat, pool, garage.
        </li>
        <li>
          <strong>Access easements cut both ways.</strong> A right of way over your land
          means traffic you can't stop; one in your favour over a neighbour's land is a
          right you should confirm is registered, not just customary.
        </li>
      </ul>

      <h2>Questions worth asking before you sign</h2>
      <ul>
        <li>Where exactly does each easement sit relative to the house and yard?</li>
        <li>Is anything already built over an easement — and was consent obtained?</li>
        <li>Do the easements leave room for what you're planning (pool, extension, shed)?</li>
        <li>Who benefits from each easement, and who maintains shared areas?</li>
      </ul>
      <p>
        None of this makes a property with easements a bad buy — the majority of suburban
        lots carry at least one. The problems come from finding out <em>after</em>{' '}
        settlement that your plans and the water authority's pipe want the same three
        metres of dirt.
      </p>
      <p>
        If you'd rather not decode the plan yourself, this is one of the things{' '}
        <Link to="/analyze">Prop Doc's analysis</Link> does automatically: it finds every
        easement disclosed anywhere in the Section 32 — title, plan schedule, authority
        certificates — and lists each one in plain English with its width and purpose.
        Our <Link to="/guides/section-32-checklist">Section 32 checklist</Link> covers the
        other nine things to verify while you're at it.
      </p>
    </>
  );
}
